import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { useAuth } from '../../hooks/useAuth';
import { clearAuthToken, getAuthToken } from '../../utils/authStorage';
import { resolveAvatarUrl } from '../../utils/avatar';
import AppAdSlot from './AppAdSlot';
import { ReactComponent as LogoMaclectic } from '../../assets/logo-horizontal.svg';

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&family=Roboto:wght@400;500&family=Roboto+Mono:wght@400;700&display=swap');
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap');
`;

const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const HeaderWrapper = styled.header`
  position: fixed;
  top: var(--beta-banner-height, 0px);
  left: 0;
  right: 0;
  z-index: 1500;
`;

const HeaderSpacer = styled.div`
  width: 100%;
  height: ${({ $height }) => $height};
`;

const TopBar = styled.div`
  background: #fbfcfb;
  border-bottom: 1px solid #e8eeea;
  padding: 0;
  max-height: 72px;
  overflow: visible;
  position: relative;
  z-index: 2;
  box-shadow: 0 2px 10px rgba(26, 46, 64, 0.035);
  transition: max-height 0.25s ease, opacity 0.2s ease, transform 0.25s ease, border-color 0.25s ease, box-shadow 0.2s ease;

  @media (max-width: 1024px) {
    padding-top: env(safe-area-inset-top, 0px);
    max-height: ${({ $compact }) => (
    $compact
      ? 'calc(56px + env(safe-area-inset-top, 0px))'
      : 'calc(64px + env(safe-area-inset-top, 0px))'
  )};
    opacity: 1;
    transform: translateY(0);
    border-bottom: 1px solid #e5e7eb;
    box-shadow: ${({ $compact }) => ($compact ? '0 6px 18px rgba(17, 24, 39, 0.08)' : 'none')};
    pointer-events: auto;
  }
`;

const TopInner = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
  padding: 0 2rem;

  @media (max-width: 1024px) {
    height: ${({ $compact }) => ($compact ? '48px' : '56px')};
    padding: 0 1rem;
  }

  @media (max-width: 380px) {
    height: 52px;
    padding: 0 max(0.75rem, env(safe-area-inset-right, 0px)) 0 max(0.75rem, env(safe-area-inset-left, 0px));
  }

  @media (min-width: 381px) and (max-width: 480px) {
    height: 52px;
    padding: 0 max(0.875rem, env(safe-area-inset-right, 0px)) 0 max(0.875rem, env(safe-area-inset-left, 0px));
  }
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  height: 100%;
  flex-shrink: 0;
  text-decoration: none;

  svg {
    width: auto;
    height: 40px;
    shape-rendering: geometricPrecision;
    color: #0A0A0A;
    transition: opacity 0.2s ease;
  }

  &:hover {
    svg {
      opacity: 0.8;
    }
  }

  @media (max-width: 1024px) {
    svg {
      height: 34px;
    }
  }
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  position: relative;
  flex-shrink: 0;

  @media (max-width: 480px) {
    gap: 0.5rem;
  }
`;

const UserAction = styled(Link)`
  background-color: #4CAF50;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-family: 'Poppins', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(76, 175, 80, 0.2);
  white-space: nowrap;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  text-decoration: none;
  padding: 0.5rem 1rem;

  &:hover {
    background-color: #388e3c;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(76, 175, 80, 0.3);
  }

  .button-text {
    display: block;
  }

  .button-icon {
    width: 18px;
    height: 18px;
    fill: currentColor;
    display: none;
  }

  @media (max-width: 1024px) {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    padding: 0;
    min-width: 44px;

    .button-text {
      display: none;
    }

    .button-icon {
      display: block;
    }
  }
`;

const UserActionButton = styled.button`
  background-color: #4CAF50;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-family: 'Poppins', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(76, 175, 80, 0.2);
  white-space: nowrap;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 0.5rem 1rem;

  &:hover {
    background-color: #388e3c;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(76, 175, 80, 0.3);
  }

  .button-text {
    display: block;
  }

  .button-icon {
    width: 18px;
    height: 18px;
    fill: currentColor;
    display: none;
  }

  @media (max-width: 1024px) {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    padding: 0;
    min-width: 44px;

    .button-text {
      display: none;
    }

    .button-icon {
      display: block;
    }
  }
`;

const RegisterAction = styled(UserAction)`
  background-color: #2a4a66;
  box-shadow: 0 2px 4px rgba(26, 46, 64, 0.2);

  &:hover {
    background-color: #2a4a66;
    transform: none;
    box-shadow: 0 2px 4px rgba(26, 46, 64, 0.2);
  }
`;

const UserStatus = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  font-size: 0.9rem;
  font-family: 'Roboto', sans-serif;
  font-weight: 500;
  color: #33485a;

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #4CAF50;
    box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.12);
  }

  @media (max-width: 1024px) {
    display: none;
  }
`;

const UserMenu = styled.div`
  position: relative;
  display: inline-block;
  z-index: 1550;
`;

const UserAvatar = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #4CAF50;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  box-shadow: 0 3px 9px rgba(26, 46, 64, 0.15);
  overflow: hidden;

  &:hover {
    background: #388e3c;
    transform: translateY(-1px);
    box-shadow: 0 5px 12px rgba(26, 46, 64, 0.2);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  @media (max-width: 1024px) {
    width: 44px;
    height: 44px;
    min-width: 44px;
  }
`;

const UserDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  min-width: 220px;
  z-index: 1400;
  display: none;
  opacity: 0;
  transform: translateY(-10px);
  transition: all 0.2s ease;

  &.show {
    display: block;
    opacity: 1;
    transform: translateY(0);
  }

  @media (max-width: 380px) {
    right: -0.5rem;
    min-width: 180px;
  }

  @media (max-width: 1024px) {
    right: -0.25rem;
    min-width: 210px;
  }
`;

const UserDropdownHeader = styled.div`
  padding: 1.25rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 12px 12px 0 0;
`;

const UserDropdownName = styled.div`
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  color: #1A2E40;
  margin: 0;
  font-size: 0.95rem;
`;

const UserDropdownEmail = styled.div`
  font-family: 'Roboto', sans-serif;
  font-size: 0.8rem;
  font-weight: 400;
  color: #666;
  margin: 0;
`;

const UserDropdownMenu = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0.5rem 0;

  li {
    margin: 0;
  }

  a, button {
    display: block;
    padding: 0.875rem 1.25rem;
    color: #333;
    text-decoration: none;
    font-family: 'Roboto', sans-serif;
    font-weight: 400;
    font-size: 0.9rem;
    transition: all 0.2s ease;
    width: 100%;
    text-align: left;
    border: none;
    background: none;
    cursor: pointer;

    &:hover {
      background: #f3f4f6;
      color: #4CAF50;
    }
  }

  .divider {
    height: 1px;
    background: #e5e7eb;
    margin: 0.5rem 0;
  }

  .logout {
    color: #E53935;

    &:hover {
      background: #fef2f2;
      color: #dc2626;
    }
  }
`;

const NavBar = styled.div`
  background: #1A2E40;
  width: 100%;
  position: relative;
  z-index: 1;
`;

const NavInner = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 2rem;
  position: relative;
  min-height: 56px;

  @media (max-width: 1024px) {
    padding: 0 max(1rem, env(safe-area-inset-right, 0px)) 0 max(1rem, env(safe-area-inset-left, 0px));
    min-height: 56px;
  }

  @media (max-width: 380px) {
    min-height: 48px;
    padding: 0 max(0.75rem, env(safe-area-inset-right, 0px)) 0 max(0.75rem, env(safe-area-inset-left, 0px));
  }

  @media (min-width: 381px) and (max-width: 480px) {
    min-height: 48px;
    padding: 0 max(0.875rem, env(safe-area-inset-right, 0px)) 0 max(0.875rem, env(safe-area-inset-left, 0px));
  }
`;

const BurgerMenu = styled.button`
  display: none;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 44px;
  height: 44px;
  border: none;
  cursor: pointer;
  z-index: 1400;
  border-radius: 6px;
  transition: background-color 0.2s ease;
  background: rgba(255, 255, 255, 0.1);
  margin-left: auto;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
  
  span {
    width: 24px;
    height: 3px;
    background: #fff;
    border-radius: 2px;
    margin: 2.5px 0;
    transition: all 0.3s cubic-bezier(.4, 0, .2, 1);
    display: block;
  }
  
  &.open span:nth-child(1) {
    transform: rotate(45deg) translate(6px, 6px);
  }

  &.open span:nth-child(2) {
    opacity: 0;
    transform: scale(0);
  }

  &.open span:nth-child(3) {
    transform: rotate(-45deg) translate(6px, -6px);
  }

  @media (max-width: 1024px) {
    display: flex;
    touch-action: manipulation;
  }
`;

const NavLinks = styled.ul`
  list-style: none;
  display: flex;
  gap: 2rem;
  justify-content: center;
  align-items: center;
  margin: 0;
  padding: 0;

  li {
    display: flex;
  }

  a {
    color: #fff;
    text-decoration: none;
    font-weight: 600;
    font-family: 'Poppins', sans-serif;
    font-size: 1rem;
    padding: 0.75rem 1rem;
    border-radius: 8px;
    transition: all 0.2s ease;
    letter-spacing: 0.01em;
    display: block;
    cursor: pointer;

    &.active,
    &:hover {
      color: #4CAF50;
      background: rgba(255, 255, 255, 0.1);
      text-decoration: none;
    }
  }

  @media (min-width: 1025px) and (max-width: 1280px) {
    gap: 1.5rem;

    a {
      font-size: 0.95rem;
      padding: 0.625rem 0.875rem;
    }
  }

  @media (min-width: 1025px) {
    gap: 2.5rem;

    a {
      font-size: 1.05rem;
      padding: 0.75rem 1.25rem;
    }
  }

  @media (max-width: 1024px) {
    display: none;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    position: absolute;
    left: 0;
    right: 0;
    top: calc(100% + 0.35rem);
    background: #fff;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
    padding: 0.5rem 0;
    z-index: 1400;
    gap: 0;
    opacity: 0;
    pointer-events: none;
    max-height: 0;
    transition: max-height 0.3s cubic-bezier(.4, 0, .2, 1), opacity 0.3s cubic-bezier(.4, 0, .2, 1);
    border-radius: 12px;
    overflow: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;

    .nav-open & {
      display: flex;
      opacity: 1;
      pointer-events: auto;
      max-height: min(72dvh, 520px);
    }

    li {
      width: 100%;
      margin: 0;
    }

    a {
      color: #333;
      background: transparent;
      width: 100%;
      padding: 1rem 1.5rem;
      font-size: 1rem;
      border-radius: 0;
      text-align: left;
      font-weight: 600;
      border: none;
      transition: all 0.2s ease;
      min-height: 48px;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      position: relative;
      border-bottom: 1px solid #f1f1f1;
      touch-action: manipulation;

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 4px;
        transform: scaleY(0);
        transition: transform 0.2s ease;
      }

      &.active::before,
      &:hover::before {
        transform: scaleY(1);
      }

      &.active,
      &:hover {
        color: #4CAF50;
        background: #f8fffe;
        transform: none;
      }
    }

    li:last-child a {
      border-bottom: none;
    }
  }
`;

const MobileBreadcrumb = styled.div`
  background: #f8f9fa;
  border-bottom: 1px solid #e5e7eb;
  padding: 1rem 0;
  display: none;
  transform: translateY(0);
  transition: transform 0.3s ease, opacity 0.3s ease;
  opacity: 1;

  &.hidden {
    transform: translateY(-100%);
    opacity: 0;
    pointer-events: none;
  }

  @media (max-width: 1024px) {
    display: block;
  }
`;

const MobileBreadcrumbInner = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;

  @media (max-width: 1024px) {
    padding: 0 1rem;
  }
`;

const MobileBreadcrumbNav = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Roboto', sans-serif;
  font-weight: 400;
  font-size: 0.9rem;

  .breadcrumb-item {
    color: #666;

    a {
      color: #4CAF50;
      text-decoration: none;
      font-weight: 500;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .breadcrumb-separator {
    color: #999;
  }

  .breadcrumb-current {
    color: #1A2E40;
    font-weight: 600;
  }
`;

const MobileSubsectionSelector = styled.div`
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  padding: 0 1rem;
  font-family: 'Roboto', sans-serif;
  font-weight: 500;
  font-size: 16px;
  color: #333;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  min-height: 48px;

  &:hover {
    border-color: #4CAF50;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
`;

const MobileDropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  z-index: 1400;
  display: none;
  margin-top: 4px;
  overflow: hidden;

  &.show {
    display: block;
    animation: ${slideDown} 0.2s ease-out;
  }

  a {
    display: block;
    padding: 1rem;
    color: #333;
    text-decoration: none;
    font-family: 'Roboto', sans-serif;
    font-weight: 400;
    font-size: 16px;
    border-bottom: 1px solid #f1f1f1;
    transition: all 0.2s ease;
    min-height: 48px;
    display: flex;
    align-items: center;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: #f8f9fa;
      color: #4CAF50;
    }

    &.active {
      background: #e8f5e9;
      color: #4CAF50;
      font-weight: 600;
    }
  }
`;

const SubNavBar = styled.div`
  background-color: #f0f4f8;
  border-bottom: 1px solid #ccc;
  padding: 0.75rem 0;
  display: none;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 1024px) {
    display: none !important;
  }
`;

const SubNavInner = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 1024px) {
    padding: 0 1rem;
  }
`;

const SubNavLinks = styled.ul`
  list-style: none;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin: 0;
  padding: 0;

  li a {
    text-decoration: none;
    color: #333;
    font-weight: 500;
    font-family: 'Roboto', sans-serif;
    transition: color 0.2s;
    font-size: 0.9rem;
    padding: 0.5rem 0;

    &:hover {
      color: #4CAF50;
    }

    &.active {
      color: #4CAF50;
      font-weight: 600;
      border-bottom: 2px solid #4CAF50;
    }
  }
`;

const Header = () => {
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [burgerOpen, setBurgerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState("landing-page");
  const [currentSub, setCurrentSub] = useState("");
  const [lastScrollTop, setLastScrollTop] = useState(0);
  const [breadcrumbHidden, setBreadcrumbHidden] = useState(false);
  const [subsectionDropdownOpen, setSubsectionDropdownOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 1024 : false
  );

  const location = useLocation();
  const scrollTimeoutRef = useRef(null);
  const headerRef = useRef(null);

  const pageNames = {
    "landing-page": "Accueil",
    nutrition: "Nutrition",
    friends: "Amis",
    goals: "Suivis",
    about: "À propos",
    "reset-password": "Mot de passe oublié",
    "verify-email": "Vérification email",
    newsletter: "Newsletter",
    admin: "Admin",
  };

  const subsectionNames = {
    nutrition: {
      journal: "Journal",
      macronutriments: "Macronutriments",
      settings: "Paramètres",
    },
    friends: {
      list: "Mes amis",
      requests: "Demandes",
      search: "Rechercher",
      blocked: "Utilisateurs bloqués",
    },
    goals: {
      sommeil: "Suivi",
      "sommeil-settings": "Paramètres",
      poids: "Suivi",
      "poids-settings": "Paramètres",
    },
  };

  const subsections = {
    nutrition: [
      { key: "journal", label: "Journal", url: "/nutrition/journal" },
      { key: "settings", label: "Paramètres", url: "/nutrition/settings" },
    ],
    friends: [
      { key: "list", label: "Mes amis", url: "/friends/list" },
      { key: "requests", label: "Demandes", url: "/friends/requests" },
      { key: "search", label: "Rechercher", url: "/friends/search" },
      { key: "blocked", label: "Utilisateurs bloqués", url: "/friends/settings" },
    ],
    goals: [
      { key: "sommeil", label: "Sommeil", url: "/sommeil" },
      { key: "sommeil-settings", label: "Paramètres sommeil", url: "/sommeil/settings" },
      { key: "poids", label: "Poids", url: "/poids" },
      { key: "poids-settings", label: "Paramètres poids", url: "/poids/settings" },
    ],
  };

  const defaultSubsections = {
    nutrition: "journal",
    friends: "list",
    goals: "sommeil",
  };

  const getGoalSectionFromSub = (subKey = "") => {
    if (subKey.startsWith("poids")) return "poids";
    if (subKey.startsWith("sommeil")) return "sommeil";
    return "sommeil";
  };

  const goalSectionConfigs = {
    sommeil: {
      navLabel: "Sommeil",
      rootUrl: "/sommeil",
      subsections: [
        { key: "sommeil", label: "Suivi", url: "/sommeil" },
        { key: "sommeil-settings", label: "Paramètres", url: "/sommeil/settings" },
      ],
    },
    poids: {
      navLabel: "Poids",
      rootUrl: "/poids",
      subsections: [
        { key: "poids", label: "Suivi", url: "/poids" },
        { key: "poids-settings", label: "Paramètres", url: "/poids/settings" },
      ],
    },
  };

  const getCurrentPageLabel = () => {
    if (currentPage !== "goals") {
      return pageNames[currentPage] || currentPage;
    }
    const goalSection = getGoalSectionFromSub(currentSub);
    return goalSectionConfigs[goalSection]?.navLabel || pageNames.goals;
  };

  const getVisibleSubsections = (pageKey, subKey = "") => {
    if (pageKey !== "goals") {
      return subsections[pageKey] || [];
    }
    const goalSection = getGoalSectionFromSub(subKey);
    return goalSectionConfigs[goalSection]?.subsections || [];
  };

  const currentGoalSection = getGoalSectionFromSub(currentSub);
  const visibleCurrentSubsections = getVisibleSubsections(currentPage, currentSub);

  const clearBodyScrollLock = useCallback(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const { body, documentElement } = document;
    body.style.overflow = '';
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
    body.style.height = '';
    body.style.overscrollBehaviorY = '';
    documentElement.style.overflow = '';
    documentElement.style.overscrollBehaviorY = '';
    documentElement.style.height = '';
  }, []);

  const handleScroll = () => {
    if (!isMobileViewport) {
      setBreadcrumbHidden(false);
      return;
    }

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollDelta = scrollTop - lastScrollTop;

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    if (scrollDelta > 10 && scrollTop > 100) {
      setBreadcrumbHidden(true);
    } else if (scrollDelta < -5 || scrollTop <= 100) {
      setBreadcrumbHidden(false);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      setLastScrollTop(scrollTop <= 0 ? 0 : scrollTop);
    }, 10);
  };

  useEffect(() => {
    let path = location.pathname.replace(/^\/+/, "");
    if (path === "") path = "landing-page";

    const pathParts = path.split("/");
    let mainPage = pathParts[0] || "landing-page";

    // Détection des nouvelles URLs /poids et /sommeil
    if (path.startsWith("poids/") || path === "poids" || path.startsWith("sommeil/") || path === "sommeil") {
      mainPage = "goals";
    }

    setCurrentPage(mainPage);

    let newSub = "";
    if (path.startsWith("nutrition/")) {
      newSub = pathParts[1] || "journal";
    } else if (mainPage === "friends") {
      const friendsSub = pathParts[1] || "";

      if (friendsSub === "settings") {
        newSub = "blocked";
      } else if (["list", "requests", "search", "blocked"].includes(friendsSub)) {
        newSub = friendsSub;
      } else {
        // Pour /friends/:username/... on garde "Mes amis" actif
        newSub = "list";
      }
    } else if (path.startsWith("goals/")) {
      const goalSection = pathParts[1] || "sommeil";
      const goalSettings = pathParts[2] || "";
      const requestedSub = goalSettings === "settings" ? `${goalSection}-settings` : goalSection;
      const validGoalSubsections = (subsections.goals || []).map((item) => item.key);
      newSub = validGoalSubsections.includes(requestedSub) ? requestedSub : "sommeil";
    } else if (path.startsWith("poids/")) {
      const goalSettings = pathParts[1] || "";
      newSub = goalSettings === "settings" ? "poids-settings" : "poids";
    } else if (path.startsWith("sommeil/")) {
      const goalSettings = pathParts[1] || "";
      newSub = goalSettings === "settings" ? "sommeil-settings" : "sommeil";
    } else if (path === "poids") {
      newSub = "poids";
    } else if (path === "sommeil") {
      newSub = "sommeil";
    }

    if (!newSub && subsections[mainPage]) {
      newSub = defaultSubsections[mainPage] || subsections[mainPage][0].key;
    }

    setCurrentSub(newSub);

    setBurgerOpen(false);
    setUserDropdownOpen(false);
    setSubsectionDropdownOpen(false);
    clearBodyScrollLock();
  }, [location, clearBodyScrollLock]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    if (burgerOpen && isMobileViewport) {
      document.body.style.overflow = 'hidden';
      return () => {
        clearBodyScrollLock();
      };
    }

    clearBodyScrollLock();
    return undefined;
  }, [burgerOpen, clearBodyScrollLock, isMobileViewport]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(max-width: 1024px)');

    const handleResize = () => {
      const nextIsMobile = mediaQuery.matches;
      setIsMobileViewport((previous) => (previous === nextIsMobile ? previous : nextIsMobile));

      if (!nextIsMobile) {
        setBurgerOpen(false);
        setSubsectionDropdownOpen(false);
        clearBodyScrollLock();
      }
    };

    const handleMediaQueryChange = (event) => {
      const nextIsMobile = event.matches;
      setIsMobileViewport((previous) => (previous === nextIsMobile ? previous : nextIsMobile));

      if (!nextIsMobile) {
        setBurgerOpen(false);
        setSubsectionDropdownOpen(false);
        clearBodyScrollLock();
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    mediaQuery.addEventListener('change', handleMediaQueryChange);
    return () => {
      window.removeEventListener('resize', handleResize);
      mediaQuery.removeEventListener('change', handleMediaQueryChange);
    };
  }, [clearBodyScrollLock]);

  useEffect(() => {
    if (!headerRef.current) {
      return undefined;
    }

    const updateHeaderHeight = () => {
      if (!headerRef.current) {
        return;
      }
      setHeaderHeight(headerRef.current.getBoundingClientRect().height);
    };

    updateHeaderHeight();

    const resizeObserver = new ResizeObserver(updateHeaderHeight);
    resizeObserver.observe(headerRef.current);
    window.addEventListener("resize", updateHeaderHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateHeaderHeight);
    };
  }, []);

  useEffect(() => {
    let scrollThrottle = null;
    const throttledScroll = () => {
      if (!scrollThrottle) {
        scrollThrottle = setTimeout(() => {
          handleScroll();
          scrollThrottle = null;
        }, 16);
      }
    };

    window.addEventListener("scroll", throttledScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", throttledScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [lastScrollTop]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".user-menu")) setUserDropdownOpen(false);
      if (!e.target.closest("#mobile-subsection-dropdown"))
        setSubsectionDropdownOpen(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const toggleBurger = () => {
    setBurgerOpen((previous) => !previous);
  };

  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = Array.isArray(user?.roles)
    && (user.roles.includes('ROLE_ADMIN') || user.roles.includes('ROLE_SUPER_ADMIN'));

  // Etat local pour l'utilisateur décodé du token JWT
  const [currentUser, setCurrentUser] = useState({
    username: "",
    email: "",
    firstname: "",
    lastname: "",
  });
  const displayUser = user || currentUser;
  const avatarUrl = resolveAvatarUrl(user?.avatar);

  // Fonction utilitaire pour décoder un JWT de façon sécurisée (usage informatif uniquement)
  // ATTENTION : Ce décodage est purement informatif côté frontend. La validation réelle du token doit TOUJOURS être réalisée côté backend.
  const decodeJwt = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error('Erreur lors du décodage du JWT :', e);
      return null;
    }
  };

  // Décodage du token JWT lors du montage (usage informatif, validation côté backend)
  useEffect(() => {
    const token = getAuthToken();
    if (token) {
      const payload = decodeJwt(token);
      if (payload && payload.exp * 1000 > Date.now()) {
        setCurrentUser({
          username: payload.username || "",
          email: payload.email || "",
          firstname: payload.firstname || "",
          lastname: payload.lastname || "",
        });
      } else {
        clearAuthToken();
        setCurrentUser({
          username: "",
          email: "",
          firstname: "",
          lastname: "",
        });
      }
    } else {
      setCurrentUser({
        username: "",
        email: "",
        firstname: "",
        lastname: "",
      });
    }
  }, []);

  const handleLogin = () => {
    if (!user) {
      navigate('/login');
    }
  };

  const handleLogout = () => {
    setUserDropdownOpen(false);
    logout();
  };

  const renderBreadcrumb = () => {
    if (!isMobileViewport || ["landing-page", "user", "register", "login"].includes(currentPage)) return null;

    const pageName = getCurrentPageLabel();
    const pageUrl =
      currentPage === "goals"
        ? goalSectionConfigs[currentGoalSection]?.rootUrl || "/sommeil"
        : subsections[currentPage] && subsections[currentPage].length > 0
          ? `/${currentPage}/${defaultSubsections[currentPage] || subsections[currentPage][0].key}`
          : `/${currentPage}`;

    const currentSubsection =
      currentSub && subsectionNames[currentPage]
        ? subsectionNames[currentPage][currentSub]
        : null;

    return (
      <MobileBreadcrumb className={breadcrumbHidden ? "hidden" : ""}>
        <MobileBreadcrumbInner>
          <MobileBreadcrumbNav>
            <span className="breadcrumb-item">
              <Link to="/">Accueil</Link>
            </span>
            <span className="breadcrumb-separator">&gt;</span>
            {currentSubsection ? (
              <span className="breadcrumb-item">
                <Link to={pageUrl}>{pageName}</Link>
              </span>
            ) : (
              <span className="breadcrumb-current">{pageName}</span>
            )}
            {currentSubsection && (
              <>
                <span className="breadcrumb-separator">&gt;</span>
                <span className="breadcrumb-current">{currentSubsection}</span>
              </>
            )}
          </MobileBreadcrumbNav>

          {visibleCurrentSubsections.length > 0 && (
            <div
              id="mobile-subsection-dropdown"
              style={{ position: "relative", display: "block" }}
            >
              <MobileSubsectionSelector
                onClick={(e) => {
                  e.stopPropagation();
                  setSubsectionDropdownOpen(!subsectionDropdownOpen);
                }}
              >
                <span>
                  {visibleCurrentSubsections.find((sub) => sub.key === currentSub)
                    ?.label || "Sélectionner une section"}
                </span>
                <svg
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                >
                  <path
                    d="M6.5 9.5 12 15l5.5-5.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </MobileSubsectionSelector>
              <MobileDropdownMenu className={subsectionDropdownOpen ? "show" : ""}>
                {visibleCurrentSubsections.map((sub) => (
                  <Link
                    key={sub.key}
                    to={sub.url}
                    className={sub.key === currentSub ? "active" : ""}
                    onClick={() => {
                      setCurrentSub(sub.key);
                      setSubsectionDropdownOpen(false);
                    }}
                  >
                    {sub.label}
                  </Link>
                ))}
              </MobileDropdownMenu>
            </div>
          )}
        </MobileBreadcrumbInner>
      </MobileBreadcrumb>
    );
  };

  return (
    <>
      <GlobalStyle />
      <HeaderWrapper role="banner" ref={headerRef}>
        <TopBar $compact={breadcrumbHidden} role="navigation" aria-label="Barre supérieure">
          <TopInner $compact={breadcrumbHidden}>
            <LogoContainer to="/">
              {/* Le SVG à gauche */}
              <LogoMaclectic />
            </LogoContainer>

            <UserSection>
              {!user ? (
                <div
                  id="logged-out-section"
                  style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}
                >
                  <RegisterAction
                    to="/register"
                    aria-label="Inscription"
                    onClick={() => {
                      setBurgerOpen(false);
                      setCurrentPage("register");
                      setCurrentSub("");
                    }}
                  >
                    <span className="button-text">Inscription</span>
                    <svg viewBox="0 0 24 24" className="button-icon">
                      <path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                    </svg>
                  </RegisterAction>
                  <UserActionButton onClick={handleLogin} aria-label="Connexion">
                    <span className="button-text">Connexion</span>
                    <svg viewBox="0 0 24 24" className="button-icon">
                      <path d="M11 7L9.6 8.4l2.6 2.6H2v2h10.2l-2.6 2.6L11 17l5-5-5-5zm9 12h-8v2h8c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-8v2h8v12z" />
                    </svg>
                  </UserActionButton>
                </div>
              ) : (
                <div
                  id="logged-in-section"
                  style={{
                    display: "flex",
                    gap: "0.75rem",
                    alignItems: "center",
                  }}
                >
                  <UserStatus aria-live="polite">
                    {displayUser.username || displayUser.email || "Mon compte"}
                  </UserStatus>
                  <UserMenu className="user-menu">
                    <UserAvatar
                      title="Menu utilisateur"
                      onClick={(e) => {
                        e.stopPropagation();
                        setUserDropdownOpen(!userDropdownOpen);
                      }}
                      tabIndex={0}
                      role="button"
                      aria-haspopup="true"
                      aria-expanded={userDropdownOpen}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setUserDropdownOpen(!userDropdownOpen);
                        }
                      }}
                    >
                      {avatarUrl ? (
                        <img src={avatarUrl} alt="Avatar" />
                      ) : (
                        displayUser.firstname || displayUser.lastname
                          ? `${(displayUser.firstname || "").charAt(0)}${(displayUser.lastname || "").charAt(0)}`.toUpperCase()
                          : displayUser.username
                            ? displayUser.username
                              .split(" ")
                              .map((n) => n[0])
                              .join("")
                              .substring(0, 2)
                              .toUpperCase()
                            : "NP"
                      )}
                    </UserAvatar>
                    <UserDropdown className={userDropdownOpen ? "show" : ""}>
                      <UserDropdownHeader>
                        <UserDropdownName>
                          {displayUser.firstname || displayUser.lastname
                            ? `${displayUser.firstname} ${displayUser.lastname}`.trim()
                            : displayUser.username
                              ? displayUser.username
                              : "Utilisateur"}
                        </UserDropdownName>
                        <UserDropdownEmail>
                          {displayUser.email || ""}
                        </UserDropdownEmail>
                      </UserDropdownHeader>
                      <UserDropdownMenu>
                        <li>
                          <Link
                            to="/users/profile"
                            onClick={() => {
                              setBurgerOpen(false);
                              setCurrentPage("profil");
                              setCurrentSub("");
                              setUserDropdownOpen(false);
                            }}
                          >
                            Mon profil
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="/users/settings"
                            onClick={() => {
                              setBurgerOpen(false);
                              setCurrentPage("settings");
                              setCurrentSub("");
                              setUserDropdownOpen(false);
                            }}
                          >
                            Paramètres
                          </Link>
                        </li>
                        {isAdmin && (
                          <li>
                            <Link
                              to="/admin"
                              onClick={() => {
                                setBurgerOpen(false);
                                setCurrentPage("admin");
                                setCurrentSub("");
                                setUserDropdownOpen(false);
                              }}
                            >
                              Espace admin
                            </Link>
                          </li>
                        )}
                        <li className="divider"></li>
                        <li>
                          <button
                            className="logout"
                            onClick={(e) => {
                              e.preventDefault();
                              handleLogout();
                            }}
                          >
                            Se déconnecter
                          </button>
                        </li>
                      </UserDropdownMenu>
                    </UserDropdown>
                  </UserMenu>
                </div>
              )}
            </UserSection>
          </TopInner>
        </TopBar>

        <NavBar className={burgerOpen ? "nav-open" : ""}>
          <NavInner>
            <NavLinks>
              <li>
                <Link
                  to="/"
                  className={currentPage === "landing-page" ? "active" : ""}
                  onClick={() => {
                    setCurrentPage("landing-page");
                    setCurrentSub("");
                    setBurgerOpen(false);
                  }}
                >
                  Accueil
                </Link>
              </li>
              <li>
                <Link
                  to="/nutrition/journal"
                  className={currentPage === "nutrition" ? "active" : ""}
                  onClick={() => {
                    setCurrentPage("nutrition");
                    setCurrentSub("journal");
                    setBurgerOpen(false);
                  }}
                >
                  Nutrition
                </Link>
              </li>
              <li>
                <Link
                  to="/friends/list"
                  className={currentPage === "friends" ? "active" : ""}
                  onClick={() => {
                    setCurrentPage("friends");
                    setCurrentSub("list");
                    setBurgerOpen(false);
                  }}
                >
                  Amis
                </Link>
              </li>
              <li>
                <Link
                  to="/sommeil"
                  className={currentPage === "goals" && currentGoalSection === "sommeil" ? "active" : ""}
                  onClick={() => {
                    setCurrentPage("goals");
                    setCurrentSub("sommeil");
                    setBurgerOpen(false);
                  }}
                >
                  Sommeil
                </Link>
              </li>
              <li>
                <Link
                  to="/poids"
                  className={currentPage === "goals" && currentGoalSection === "poids" ? "active" : ""}
                  onClick={() => {
                    setCurrentPage("goals");
                    setCurrentSub("poids");
                    setBurgerOpen(false);
                  }}
                >
                  Poids
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  className={currentPage === "about" ? "active" : ""}
                  onClick={() => {
                    setCurrentPage("about");
                    setCurrentSub("");
                    setBurgerOpen(false);
                  }}
                >
                  À propos
                </Link>
              </li>
            </NavLinks>
            <BurgerMenu
              className={burgerOpen ? "open" : ""}
              onClick={toggleBurger}
              aria-label="Menu"
              aria-controls="nav-links"
              aria-expanded={burgerOpen}
            >
              <span></span>
              <span></span>
              <span></span>
            </BurgerMenu>
          </NavInner>
        </NavBar>

        {renderBreadcrumb()}

        {currentPage === "nutrition" && !isMobileViewport && (
          <SubNavBar style={{ display: "block" }}>
            <SubNavInner>
              <SubNavLinks>
                {subsections.nutrition.map((sub) => (
                  <li key={sub.key}>
                    <Link
                      to={sub.url}
                      className={currentSub === sub.key ? "active" : ""}
                      onClick={() => setCurrentSub(sub.key)}
                    >
                      {sub.label}
                    </Link>
                  </li>
                ))}
              </SubNavLinks>
            </SubNavInner>
          </SubNavBar>
        )}

        {currentPage === "friends" && !isMobileViewport && (
          <SubNavBar style={{ display: "block" }}>
            <SubNavInner>
              <SubNavLinks>
                {subsections.friends.map((sub) => (
                  <li key={sub.key}>
                    <Link
                      to={sub.url}
                      className={currentSub === sub.key ? "active" : ""}
                      onClick={() => setCurrentSub(sub.key)}
                    >
                      {sub.label}
                    </Link>
                  </li>
                ))}
              </SubNavLinks>
            </SubNavInner>
          </SubNavBar>
        )}

        {currentPage === "goals" && !isMobileViewport && (
          <SubNavBar style={{ display: "block" }}>
            <SubNavInner>
              <SubNavLinks>
                {(goalSectionConfigs[currentGoalSection]?.subsections || []).map((sub) => (
                  <li key={sub.key}>
                    <Link
                      to={sub.url}
                      className={currentSub === sub.key ? "active" : ""}
                      onClick={() => setCurrentSub(sub.key)}
                    >
                      {sub.label}
                    </Link>
                  </li>
                ))}
              </SubNavLinks>
            </SubNavInner>
          </SubNavBar>
        )}

      </HeaderWrapper>
      <HeaderSpacer
        $height={`calc(var(--beta-banner-height, 0px) + ${headerHeight}px)`}
        aria-hidden="true"
      />
      <AppAdSlot placement="header" />
    </>
  );
};

export default Header;
